import * as Device from 'expo-device';
import { EventSubscription } from 'expo-modules-core';
import * as Notifications from 'expo-notifications';
import { router } from 'expo-router';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Platform, Linking, AppState } from 'react-native';

import { NotificationModal } from '@/components/notification-modal';
import { apiService } from '@/services/api-service';
import { authService } from '@/services/auth-service';
import { reconcileUrgentBadge } from '@/services/badge-service';
import { logger } from '@/utils/logger';

// Define the shape of the context data
interface NotificationContextData {
	expoPushToken: string | null;
	notification: Notifications.Notification | null;
	showNotificationModal: (title: string, body: string, type?: 'update' | 'workorder' | 'general', workOrderId?: string) => void;
}

// Create the context
const NotificationContext = createContext<NotificationContextData | undefined>(undefined);

// Default notification handler configuration
Notifications.setNotificationHandler({
	handleNotification: async (notification) => {
		// Check if this is an urgent work order notification
		const isUrgent = notification.request.content.title?.includes('URGENT:') || false;

		return {
			shouldShowAlert: true,
			shouldPlaySound: isUrgent, // Play sound for urgent notifications
			shouldSetBadge: isUrgent, // Set badge for urgent notifications
			shouldShowBanner: true,
			shouldShowList: true,
		};
	},
});

// Helper function to register for push notifications
async function registerForPushNotificationsAsync(): Promise<string | null> {
	let token: string | null = null;

	// Handle web platform
	if (Platform.OS === 'web') {
		// Check if the browser supports notifications
		if ('Notification' in window) {
			const permission = await Notification.requestPermission();
			if (permission === 'granted') {
				console.warn('Web notifications enabled');
				// For web, we don't have an Expo push token, but we can still handle notifications
				return 'web-notifications-enabled';
			} else {
				console.warn('Web notifications permission denied');
			}
		} else {
			console.warn('This browser does not support notifications');
		}
		return null;
	}

	if (Platform.OS === 'android') {
		await Notifications.setNotificationChannelAsync('default', {
			name: 'default',
			importance: Notifications.AndroidImportance.MAX,
			vibrationPattern: [0, 250, 250, 250],
			lightColor: '#FF231F7C',
		});
	}

	if (Device.isDevice) {
		const { status: existingStatus } = await Notifications.getPermissionsAsync();
		let finalStatus = existingStatus;
		if (existingStatus !== 'granted') {
			const { status } = await Notifications.requestPermissionsAsync();
			finalStatus = status;
		}
		if (finalStatus !== 'granted') {
			console.warn('Failed to get push token for push notification!');
			return null;
		}
		// Learn more about projectId: https://docs.expo.dev/push-notifications/push-notifications-setup/#configure-projectid
		// TODO: Consider moving projectId to environment variables or config
		token = (await Notifications.getExpoPushTokenAsync({
			projectId: 'ed0d543e-a6e0-41c6-991a-635772210de6',
		})).data;
		console.warn('Expo Push Token:', token);
	} else {
		console.warn('Push notifications are unavailable on this simulator');
	}

	return token;
}

// Only genuine Expo push tokens (`ExponentPushToken[...]`) are accepted by the
// backend. Web returns a sentinel ('web-notifications-enabled') and simulators
// can return non-token values — registering those just 400s, so skip them.
function isExpoPushToken(token: string): boolean {
	return token.startsWith('ExponentPushToken[') && token.endsWith(']');
}

// Helper function to register Expo push token with backend
async function registerExpoPushTokenWithBackend(expoPushToken: string): Promise<void> {
	logger.log('=== CLIENT: REGISTERING EXPO PUSH TOKEN ===');
	logger.log('Expo push token:', expoPushToken);

	try {
		// Get the current device token from the auth service
		const session = await authService.getSession();
		logger.log('Current session:', {
			hasSession: !!session,
			hasDeviceToken: !!session?.deviceToken,
			deviceTokenPreview: session?.deviceToken?.substring(0, 8) + '...',
			userEmail: session?.email,
		});

		if (!session?.deviceToken) {
			console.warn('CLIENT: No device token available for push token registration');
			return;
		}

		logger.log('CLIENT: Sending registration request to backend...');

		// Register the Expo push token with the backend
		const response = await apiService.fetchApi('/device-tokens/register-push', {
			method: 'POST',
			body: JSON.stringify({
				deviceToken: session.deviceToken,
				expoPushToken,
			}),
		});

		logger.log('CLIENT: Backend response:', response);
		logger.log('CLIENT: Expo push token registered successfully');

	} catch (error) {
		console.error('CLIENT: Failed to register Expo push token:', error);
		console.error('CLIENT: Error details:', {
			message: error instanceof Error ? error.message : 'Unknown error',
			stack: error instanceof Error ? error.stack : 'No stack trace',
		});
	}
}

// Provider component
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [expoPushToken, setExpoPushToken] = useState<string | null>(null);
	const [notification, setNotification] = useState<Notifications.Notification | null>(null);
	const [modalVisible, setModalVisible] = useState(false);
	const [modalData, setModalData] = useState<{
		title: string;
		body: string;
		type: 'update' | 'workorder' | 'general';
		workOrderId?: string;
	} | null>(null);
	const notificationListener = useRef<EventSubscription | null>(null);
	const responseListener = useRef<EventSubscription | null>(null);

	const showNotificationModal = (title: string, body: string, type: 'update' | 'workorder' | 'general' = 'general', workOrderId?: string) => {
		setModalData({ title, body, type, workOrderId });
		setModalVisible(true);
	};

	useEffect(() => {
		let cancelled = false;

		// Skip native notification setup on iOS/Android simulators — the
		// PushNotificationIOS native module is absent and NativeEventEmitter
		// crashes the JS runtime at module-load time.
		if (Platform.OS !== 'web' && !Device.isDevice) {
			return;
		}

		registerForPushNotificationsAsync().then(token => {
			if (cancelled) return;
			if (token) {
				setExpoPushToken(token);
				// Web/simulator sentinels aren't real push tokens — registering them
				// just 400s the backend. Only send genuine ExponentPushToken[...] values.
				if (isExpoPushToken(token)) registerExpoPushTokenWithBackend(token);
			}
		});

		// Issue #89: reconcile the badge to actual urgent count instead of
		// blindly clearing. Previously this set the badge to 0 on every
		// mount, which masked legitimate urgent WOs while the app was
		// backgrounded and caused the badge to lag behind reality.
		reconcileUrgentBadge();

		// Listener for received notifications while the app is foregrounded
		notificationListener.current = Notifications.addNotificationReceivedListener(notification => {
			setNotification(notification);
			console.warn('Notification Received:', notification);
			// TODO: Handle received notification (e.g., update UI, show in-app message)
		});

		// Listener for user interaction with notifications (tapping)
		responseListener.current = Notifications.addNotificationResponseReceivedListener(response => {
			logger.log('CLIENT: Notification Response Received:', response);

			// Reconcile badge to truth on tap (not blind-clear) so any
			// other still-urgent WOs keep their badge count.
			reconcileUrgentBadge();

			const notificationData = response.notification.request.content.data;
			logger.log('CLIENT: Notification data:', notificationData);

			if (notificationData) {
				const { type, action, workOrderId, url } = notificationData;

				switch (action) {
					case 'testflight':
						logger.log('CLIENT: Opening TestFlight');
						if (url && typeof url === 'string') {
							Linking.openURL(url).catch(err => {
								console.error('CLIENT: Failed to open TestFlight:', err);
								// Fallback: show modal
								showNotificationModal(
									response.notification.request.content.title || 'App Update Available',
									response.notification.request.content.body || 'Please update the app',
									'update',
								);
							});
						}
						break;

					case 'workorder':
						logger.log('CLIENT: Navigating to work order:', workOrderId);
						if (workOrderId && typeof workOrderId === 'string') {
							try {
								// Navigate to the specific work order using Expo Router
								router.push({
									pathname: '/work-orders/[id]',
									params: { id: workOrderId },
								});
								logger.log('CLIENT: Successfully navigated to work order:', workOrderId);
							} catch (error) {
								console.error('CLIENT: Failed to navigate to work order:', error);
								// Fallback: show modal with work order info
								showNotificationModal(
									response.notification.request.content.title || 'Work Order Notification',
									response.notification.request.content.body || 'You have a work order update',
									'workorder',
									workOrderId,
								);
							}
						} else {
							console.warn('CLIENT: No work order ID provided for work order notification');
							showNotificationModal(
								response.notification.request.content.title || 'Work Order Notification',
								response.notification.request.content.body || 'You have a work order update',
								'workorder',
								typeof workOrderId === 'string' ? workOrderId : undefined,
							);
						}
						break;

					case 'modal':
					default:
						logger.log('CLIENT: Showing notification modal');
						showNotificationModal(
							response.notification.request.content.title || 'Notification',
							response.notification.request.content.body || 'You have a new notification',
							(typeof type === 'string' && (type === 'update' || type === 'workorder' || type === 'general')) ? type : 'general',
						);
						break;
				}
			} else {
				// Fallback: show basic modal
				showNotificationModal(
					response.notification.request.content.title || 'Notification',
					response.notification.request.content.body || 'You have a new notification',
					'general',
				);
			}
		});

		// Issue #89: also reconcile when the app foregrounds — iOS keeps
		// the badge visible while we're backgrounded, and the count there
		// can go stale as other users complete urgent WOs.
		const appStateSubscription = AppState.addEventListener('change', state => {
			if (state === 'active') reconcileUrgentBadge();
		});

		// Web-specific notification handling
		let webNotificationCleanup: (() => void) | null = null;
		if (Platform.OS === 'web' && 'Notification' in window) {
			// Handle web notification clicks
			const handleWebNotificationClick = (event: Event) => {
				console.warn('Web notification clicked:', event);
				// TODO: Handle web notification click (e.g., focus window, navigate to specific screen)
			};

			// Add event listener for web notifications
			window.addEventListener('notificationclick', handleWebNotificationClick);
			webNotificationCleanup = () => {
				window.removeEventListener('notificationclick', handleWebNotificationClick);
			};
		}

		// Cleanup listeners on unmount
		return () => {
			cancelled = true;
			if (notificationListener.current) {
				notificationListener.current.remove();
			}
			if (responseListener.current) {
				responseListener.current.remove();
			}
			if (webNotificationCleanup) {
				webNotificationCleanup();
			}
			appStateSubscription.remove();
		};
	}, []);

	const contextValue: NotificationContextData = {
		expoPushToken,
		notification,
		showNotificationModal,
	};

	return (
		<NotificationContext.Provider value={contextValue}>
			{children}

			{/* In-app notification modal */}
			{modalData && (
				<NotificationModal
					visible={modalVisible}
					onDismiss={() => {
						setModalVisible(false);
						setModalData(null);
					}}
					title={modalData.title}
					body={modalData.body}
					type={modalData.type}
					workOrderId={modalData.workOrderId}
				/>
			)}
		</NotificationContext.Provider>
	);
};

// Custom hook to use the notification context
export const useNotifications = (): NotificationContextData => {
	const context = useContext(NotificationContext);
	if (context === undefined) {
		throw new Error('useNotifications must be used within a NotificationProvider');
	}
	return context;
};
